import type { RequireProps } from '@app-types/require'
import { isBrowser } from '@shared/util/env'
import type { LocalLink } from '@shared/util/link'
import type { ServerResponse } from 'http'
import type { NextRouter } from 'next/router'
import type { ParsedUrlQuery } from 'querystring'

export class PageError extends Error {
    constructor(
        public statusCode: number,
        public description?: string,
        message?: string,
    ) {
        super(message)
    }

    /**
     * Executes `fn()` on the client and server, catching and handling RedirectErrors.
     * Will rethrow any other errors.
     */
    static async try(context: ErrorContext, fn: () => Promise<void>) {
        try {
            await fn()
        } catch (error) {
            if (error instanceof PageError && context.res) {
                context.res.statusCode = error.statusCode
            }

            if (!(error instanceof RedirectError)) {
                // Make sure we're always throwing an error object.
                if (error instanceof Error) {
                    throw error
                }

                throw new Error(`A non-error was thrown: ${error}`)
            }

            if (isBrowser()) {
                error.handleOnClient(
                    context as RequireProps<ErrorContext, 'router'>,
                )
                return
            }

            if (context.res)
                error.handleOnServer(
                    context as RequireProps<ErrorContext, 'res'>,
                )
        }
    }
}

export class NotFoundError extends PageError {
    constructor(description?: string, message?: string) {
        super(404, description, message)
    }

    toNextProps() {
        return { notFound: true } as const
    }
}

export class BadRequestError extends PageError {
    constructor(description?: string, message?: string) {
        super(400, description, message)
    }
}

export class ServerTimeoutError extends PageError {
    constructor(description?: string, message?: string) {
        super(504, description, message)
    }
}

export class ServerError extends PageError {
    constructor(
        description = 'Internal server error',
        message?: string,
        stack?: string,
    ) {
        super(500, description, message)
        this.stack = stack
    }
}

export class RateLimitError extends PageError {
    constructor(description?: string, message?: string) {
        super(429, description ?? 'Too many requests', message)
    }

    toNextProps() {
        // Redirect to ERROR_504 (Gateway Timeout) page
        // Semantically appropriate for "server temporarily unavailable"
        return {
            redirect: {
                destination: '/504',
                permanent: false,
            },
        } as const
    }
}

export class RedirectError extends PageError {
    constructor(
        public link: LocalLink,
        message?: string,
    ) {
        super(302, undefined, message)
    }

    handleOnClient({ router }: RequireProps<ErrorContext, 'router'>) {
        this.link.query = { ...router.query, ...this.link.query }
        router.replace(this.link.serialize(router))
    }

    handleOnServer({ res, query }: RequireProps<ErrorContext, 'res'>) {
        res.statusCode = 302
        this.link.query = { ...query, ...this.link.query }
        res.writeHead(302, {
            Location: encodeURI(this.link.serialize()),
        })
        res.end()
    }

    toNextProps() {
        return {
            redirect: {
                destination: this.link.serialize(),
                permanent: false,
            },
        }
    }
}

interface ErrorContext {
    router?: NextRouter
    res?: ServerResponse
    query?: ParsedUrlQuery
}
